import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { NAV_LINKS } from '@/lib/constants'


interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
}

export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const location = useLocation()
  const [isAnimating, setIsAnimating] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)


  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      // Trigger animation after render with a small delay
      const timer = setTimeout(() => {
        setIsAnimating(true)
      }, 10) // Small delay to ensure DOM is rendered
      return () => clearTimeout(timer)
    } else {
      setIsAnimating(false)
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false)
      }, 300) // Match animation duration
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!shouldRender) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        background: '#0B1B25',
        transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
        opacity: isAnimating ? 1 : 0,
        transition: 'transform 300ms ease-in-out, opacity 300ms ease-in-out',
      }}
    >
      {/* Layer 1: Martin Martz Background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'url("/images/martin-martz-R_9BV4WRphI-unsplash%201.png")',
          backgroundSize: '140% 100%',
          backgroundPosition: 'center',
          zIndex: 0,
        }}
      />

      {/* Layer 2: Noisy Gradients */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url("/images/noisy-gradients.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mixBlendMode: 'overlay',
          zIndex: 1,
        }}
      />

      {/* Layer 3: Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.01), rgba(0, 0, 0, 0.01)), rgba(0, 0, 0, 0.2)',
          mixBlendMode: 'normal',
          zIndex: 2,
        }}
      />

      {/* Layer 4: Nav Bar (Blur Layer) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.39)',
          mixBlendMode: 'darken',
          backdropFilter: 'blur(28.8px)',
          WebkitBackdropFilter: 'blur(28.8px)',
          zIndex: 3,
        }}
      />

      {/* Header inside Menu (Logo + Close) */}
      <div
        style={{
          position: 'absolute',
          top: '62px',
          left: '16px',
          right: '16px',
          height: '48px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10001,
        }}
      >
        <Link
          to="/"
          onClick={onClose}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '12px',
            textDecoration: 'none',
          }}
        >
          <img
            src="/images/SVGs/logo.svg"
            alt="DIGITREK Logo"
            style={{ width: '24px', height: '26px' }}
          />
          <div
            style={{
              fontFamily: "'Instrument Sans', sans-serif",
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '27px',
              letterSpacing: '2px',
              color: '#F4F0EF',
            }}
          >
            DIGITREK
          </div>
        </Link>

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px',
            width: '48px',
            height: '48px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
          aria-label="Close menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Menu Options */}
      <nav
        style={{
          position: 'absolute',
          width: '100%',
          maxWidth: '402px',
          left: '50%',
          transform: 'translateX(-50%)',
          top: '110px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '16px 0px',
          zIndex: 10002,
        }}
      >
        {NAV_LINKS.map((link) => {
          const isHome = link.href === '#home';
          const active = isHome
            ? location.pathname === '/' && (!location.hash || location.hash === '#home')
            : location.hash === link.href;

          return (
            <Link
              key={link.href}
              to={isHome ? '/' : `/${link.href}`}
              onClick={onClose}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '16px 24px',
                gap: '10px',
                width: '100%',
                height: '61px',
                textDecoration: 'none',
              }}
            >
              <span
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontWeight: 400,
                  fontSize: '24px',
                  lineHeight: '29px',
                  color: active ? '#FFFFFF' : '#939090',
                }}
              >
                {link.label}
              </span>
            </Link>
          )
        })}
      </nav>

      {/* Footer Text */}
      <div
        style={{
          position: 'absolute',
          width: '218px',
          height: '32px',
          left: 'calc(50% - 218px/2)',
          top: 'calc(50% - 32px/2 + 397px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 10002,
        }}
      >
        <span
          style={{
            fontFamily: "'Instrument Sans', sans-serif",
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '130%',
            letterSpacing: '-0.02em',
            color: '#FFFFFF',
          }}
        >
          © 2026 All Rights Reserved.
        </span>
      </div>
    </div>
  )
}



